import DraggableMarker from 'components/BaseMap/components/DraggableMarker';
import { useCallback } from 'react';
import { Popup } from 'react-leaflet';
import { useDispatch } from 'react-redux';
import { useGetJurisdictionsQuery } from 'services/geography/geography.api';
import { moveAlertMarker } from '../../../../state/slices/alertCreatorSlice';
import { isMarkerInJurisdiction } from '../../utils/alertMapEditor.utils';

/**
 * Adds alert-specific behavior to the shared draggable marker. Existing RescAlert points are saved
 * to the shared draft, while IPAWS shape handles can provide their own preview and save callbacks.
 *
 * @param {Object} props
 * @param {{lat: number, lng: number}} props.position Current marker position.
 * @param {number} props.markerIndex Position of this marker in its owning list.
 * @param {Function} [props.onMarkerMove] Optional final update used by IPAWS shape editing.
 * @param {Function} [props.onMarkerMovePreview] Optional temporary update used during a drag.
 * @param {boolean} [props.showWorkplaceJurisdictionWarning=true] Whether to compare the marker
 * against the workplace's RescAlert jurisdiction boundaries.
 * @param {string} [props.accessibleName] Clear name for assistive technology.
 * @param {boolean} [props.keyboard] Enables Leaflet keyboard focus when true.
 * @returns {JSX.Element|null}
 */
const AlertPointMarker = ({
  position,
  markerIndex,
  onMarkerMove,
  onMarkerMovePreview,
  showWorkplaceJurisdictionWarning = true,
  accessibleName,
  keyboard,
  onClick,
  draggable = true,
}) => {
  const { data: jurisdictions = [] } = useGetJurisdictionsQuery();
  const dispatch = useDispatch();
  const isOutOfJurisdiction =
    showWorkplaceJurisdictionWarning && !isMarkerInJurisdiction(position, jurisdictions);

  // Save only after the user releases the marker. Live movement uses the separate preview callback.
  const handleMarkerDragEnd = useCallback(
    (index, newPosition) => {
      if (typeof index !== 'number' || !newPosition) return;

      if (showWorkplaceJurisdictionWarning && !isMarkerInJurisdiction(newPosition, jurisdictions)) {
        console.warn(
          `⚠️ Warning: Marker dragged outside jurisdiction boundaries at coordinates [${newPosition?.lat?.toFixed?.(6)}, ${newPosition?.lng?.toFixed?.(6)}]`,
        );
      }

      // IPAWS shapes own their update rules. Other alert points use the original draft update.
      if (onMarkerMove) {
        return onMarkerMove(index, newPosition);
      }

      dispatch(moveAlertMarker({ index, newPosition }));
      return true;
    },
    [jurisdictions, dispatch, onMarkerMove, showWorkplaceJurisdictionWarning],
  );

  const handleMarkerDrag = useCallback(
    (index, newPosition) => onMarkerMovePreview?.(index, newPosition),
    [onMarkerMovePreview],
  );

  if (!position) return null;

  return (
    <DraggableMarker
      // Leaflet reads title and alt only on creation, so refresh the marker after a rename.
      key={accessibleName}
      position={position}
      onMarkerDrag={onMarkerMovePreview ? handleMarkerDrag : undefined}
      onMarkerDragEnd={handleMarkerDragEnd}
      markerIndex={markerIndex}
      showErrorIcon={isOutOfJurisdiction}
      accessibleName={accessibleName ?? `Move alert boundary point ${markerIndex + 1}`}
      keyboard={keyboard}
      onClick={onClick}
      draggable={draggable}
    >
      {isOutOfJurisdiction && (
        <Popup>
          <div>
            <strong>⚠️ Out of Jurisdiction</strong>
            <br />
            This marker is outside the jurisdiction boundaries.
            <br />
            Coordinates: [{position?.lat?.toFixed?.(6) ?? 'N/A'},{' '}
            {position?.lng?.toFixed?.(6) ?? 'N/A'}]
          </div>
        </Popup>
      )}
    </DraggableMarker>
  );
};

export default AlertPointMarker;
